import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { CalendarClock, Loader2, AlertTriangle, CheckCircle, PlayCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function FechaPagoSyncCard() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/management/payment-log/sync-fecha-pago/status`,
        { withCredentials: true }
      );
      setState(data);
      return data;
    } catch (e) {
      // Permiso insuficiente → ocultamos la card
      if (e.response?.status === 403) return null;
      setError(e.response?.data?.detail || 'Error consultando estado');
      return null;
    }
  };

  useEffect(() => {
    refresh();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Poll cada 1.5 s mientras el job está corriendo
  useEffect(() => {
    if (state?.status === 'running') {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(refresh, 1500);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current && state?.status !== 'running') {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [state?.status]);

  const handleStart = async () => {
    setStarting(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/management/payment-log/sync-fecha-pago`,
        {},
        { withCredentials: true }
      );
      setState(data.state);
      setConfirmOpen(false);
      if (!data.started) {
        setError('Ya hay un sync en curso. Espera a que termine.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo iniciar la sincronización');
    } finally {
      setStarting(false);
    }
  };

  const isRunning = state?.status === 'running';
  const isDone = state?.status === 'done';
  const isError = state?.status === 'error';
  const total = state?.total || 0;
  const processed = state?.processed || 0;
  const updated = state?.updated || 0;
  const noDate = state?.no_date_found || 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;

  // 0 progreso cuando el status no se cargó = probablemente sin permisos → esconder
  if (state === null && !error) return null;

  return (
    <Card className="bg-white rounded-xl border border-[#E8E5DD]" data-testid="fecha-pago-sync-card">
      <CardHeader>
        <CardTitle className="text-xl text-[#1A1C18] flex items-center gap-2">
          <CalendarClock size={22} className="text-[#8B5CF6]" />
          Sincronizar fechas de pago históricas
        </CardTitle>
        <p className="text-sm text-[#6A736B] mt-1">
          Rellena <code className="text-xs">fecha_pago</code> con la fecha real de la boleta Medilink
          para cada abono del historial que no la tenga. Respeta el delay de 1.5 s para no gatillar
          <strong> rate limit</strong>. Si Medilink no entrega fecha, se deja <code className="text-xs">None</code>
          — nunca se usa la hora de ejecución.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={isRunning}
            className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
            data-testid="fecha-pago-sync-start-btn"
          >
            {isRunning ? <Loader2 size={14} className="mr-2 animate-spin" /> : <PlayCircle size={14} className="mr-2" />}
            {isRunning ? 'Sincronización en curso…' : 'Sincronizar fechas de pago históricas'}
          </Button>
          {isDone && !isRunning && state?.finished_at && (
            <span className="text-xs text-[#6A736B] inline-flex items-center gap-1">
              <CheckCircle size={14} className="text-emerald-600" />
              Última ejecución: {new Date(state.finished_at).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}
            </span>
          )}
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {state && (isRunning || isDone || isError) && (
          <div
            className={`rounded-lg border p-4 ${
              isError ? 'bg-red-50 border-red-200' : isRunning ? 'bg-blue-50 border-blue-200' : 'bg-emerald-50 border-emerald-200'
            }`}
            data-testid="fecha-pago-sync-progress"
          >
            <div className="flex flex-wrap gap-4 text-sm">
              <p><span className="font-semibold">Estado:</span> {isRunning ? 'En progreso' : isDone ? 'Completado' : 'Error'}</p>
              <p><span className="font-semibold">Total:</span> {total}</p>
              <p><span className="font-semibold">Procesados:</span> {processed}</p>
              <p className="text-emerald-700"><span className="font-semibold">Actualizados:</span> {updated}</p>
              <p className="text-amber-700"><span className="font-semibold">Sin fecha Medilink:</span> {noDate}</p>
            </div>
            {total > 0 && (
              <div className="mt-3">
                <div className="w-full h-2 bg-white rounded-full overflow-hidden border border-[#E8E5DD]">
                  <div
                    className={`h-full ${isError ? 'bg-red-500' : 'bg-[#8B5CF6]'} transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-xs text-[#6A736B] mt-1">{pct}% ({processed} / {total})</p>
              </div>
            )}
            {isDone && !isRunning && (
              <p className="mt-3 text-sm text-[#1A1C18]" data-testid="fecha-pago-sync-summary">
                <strong>Resumen final:</strong> {updated} de {processed} registros actualizados
                {noDate > 0 ? ` · ${noDate} sin fecha encontrada en Medilink` : ''}.
              </p>
            )}
            {isError && state?.error && (
              <p className="mt-3 text-sm text-red-700">Error: {state.error}</p>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(false)}>
        <DialogContent data-testid="fecha-pago-sync-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              Confirmar sincronización
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#1A1C18]">
            <p>
              Se recorrerán <strong>todas las entradas del historial</strong> con
              <code className="mx-1 text-xs">fecha_pago = None</code> y se consultará Medilink
              por la fecha real de la boleta. Con delay de 1.5 s por llamada, puede tardar varios
              minutos si hay muchos registros.
            </p>
            <p className="text-[#6A736B]">
              No se modifica ningún otro campo. Si Medilink no entrega fecha para una entrada,
              se deja <code className="text-xs">None</code>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={starting}>
              Cancelar
            </Button>
            <Button
              onClick={handleStart}
              disabled={starting}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
              data-testid="fecha-pago-sync-confirm-btn"
            >
              {starting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <PlayCircle size={14} className="mr-2" />}
              Iniciar sincronización
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
